<!-- Hero Banner -->
<div class="bg-slate-50 border-b border-gray-200 py-12 px-6 text-center">
  <div class="max-w-3xl mx-auto">
    <span
      class="daisy-badge daisy-badge-outline text-[10px] font-bold tracking-widest uppercase py-3 px-4 mb-4 bg-white border-gray-300 text-gray-600"
    >
      ZOOM MARKETPLACE DOCUMENTATION
    </span>
    <h1 class="text-4xl font-extrabold text-gray-900 leading-tight mb-3">
      Zoom Integration & User Guide
    </h1>
    <p class="text-[15px] text-gray-500 leading-relaxed max-w-2xl mx-auto">
      Comprehensive documentation for connecting, configuring, and using the
      Learnbird Zoom Integration app for hosting and joining interactive live
      online classes.
    </p>
  </div>
</div>

<!-- Page Body -->
<div
  class="flex flex-col md:flex-row gap-10 max-w-6xl mx-auto px-6 py-10 md:py-16 items-start"
>
  <!-- Left Sidebar -->
  <aside class="w-full md:w-64 md:shrink-0 md:sticky md:top-20">
    <div
      class="daisy-card bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
    >
      <h2 class="text-base font-bold text-gray-900 mb-2">At a Glance</h2>
      <p class="text-xs text-gray-500 leading-relaxed mb-4">
        Official user guide and integration documentation for the Learnbird Zoom
        Marketplace App.
      </p>

      <div class="flex flex-col gap-3 pb-4 border-b border-gray-100 mb-4">
        <div class="flex gap-2.5 items-start text-xs text-gray-700">
          <i class="fa-solid fa-calendar text-gray-400 mt-0.5 shrink-0"></i>
          <div>
            <span class="block font-semibold text-gray-700">Last Updated</span>
            <span class="block text-gray-500">August 2026</span>
          </div>
        </div>
        <div class="flex gap-2.5 items-start text-xs text-gray-700">
          <i class="fa-solid fa-building text-gray-400 mt-0.5 shrink-0"></i>
          <div>
            <span class="block font-semibold text-gray-700">Publisher</span>
            <span class="block text-gray-500">CEYBYTE (Pvt) Ltd</span>
          </div>
        </div>
        <div class="flex gap-2.5 items-start text-xs text-gray-700">
          <i class="fa-solid fa-envelope text-gray-400 mt-0.5 shrink-0"></i>
          <div>
            <span class="block font-semibold text-gray-700"
              >Technical Support</span
            >
            <a
              href="mailto:support@learnbird.lk"
              class="block text-blue-600 hover:underline break-all"
            >
              support&#64;learnbird.lk
            </a>
          </div>
        </div>
      </div>

      <!-- Table of Contents -->
      <div>
        <h3
          class="text-[10px] font-bold tracking-wider text-gray-400 uppercase mb-2.5"
        >
          DOCUMENTATION SECTIONS
        </h3>
        <nav class="flex flex-col gap-1">
          <a
            href="zoom-documentation#overview"
            class="block text-xs text-gray-700 hover:bg-gray-100 hover:text-gray-900 px-2 py-1.5 rounded-lg transition-colors duration-150"
          >
            1. Overview & Features
          </a>
          <a
            href="zoom-documentation#prerequisites"
            class="block text-xs text-gray-700 hover:bg-gray-100 hover:text-gray-900 px-2 py-1.5 rounded-lg transition-colors duration-150"
          >
            2. Prerequisites & Requirements
          </a>
          <a
            href="zoom-documentation#installation"
            class="block text-xs text-gray-700 hover:bg-gray-100 hover:text-gray-900 px-2 py-1.5 rounded-lg transition-colors duration-150"
          >
            3. Installation & Authorization
          </a>
          <a
            href="zoom-documentation#usage"
            class="block text-xs text-gray-700 hover:bg-gray-100 hover:text-gray-900 px-2 py-1.5 rounded-lg transition-colors duration-150"
          >
            4. How to Use the App
          </a>
          <a
            href="zoom-documentation#uninstallation"
            class="block text-xs text-gray-700 hover:bg-gray-100 hover:text-gray-900 px-2 py-1.5 rounded-lg transition-colors duration-150"
          >
            5. De-authorization & Uninstall
          </a>
          <a
            href="zoom-documentation#security"
            class="block text-xs text-gray-700 hover:bg-gray-100 hover:text-gray-900 px-2 py-1.5 rounded-lg transition-colors duration-150"
          >
            6. Data Security & Privacy
          </a>
          <a
            href="zoom-documentation#troubleshooting"
            class="block text-xs text-gray-700 hover:bg-gray-100 hover:text-gray-900 px-2 py-1.5 rounded-lg transition-colors duration-150"
          >
            7. Troubleshooting & Contact
          </a>
        </nav>
      </div>
    </div>
  </aside>

  <!-- Main Content -->
  <main class="flex-1 min-w-0 space-y-12">
    <!-- Section 1: Overview -->
    <section id="overview" class="scroll-mt-20">
      <div class="flex items-center gap-2 mb-3">
        <div
          class="w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 font-bold text-sm shrink-0"
        >
          1
        </div>
        <h2 class="text-xl font-bold text-gray-900">
          App Overview & Key Features
        </h2>
      </div>
      <p class="text-sm text-gray-600 leading-relaxed mb-4">
        The <strong>Learnbird Zoom Integration</strong> connects your Zoom
        account with the Learnbird e-learning platform. It enables tutors,
        instructors, and educational institutions to effortlessly schedule,
        host, and conduct live interactive video classes directly within their
        Learnbird virtual workspace.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
        <div
          class="daisy-card bg-slate-50 border border-gray-200 p-4 rounded-xl"
        >
          <div class="flex items-center gap-3 mb-2">
            <i class="fa-solid fa-calendar-plus text-blue-600 text-base"></i>
            <h3 class="font-bold text-gray-800 text-sm">
              Automated Meeting Scheduling
            </h3>
          </div>
          <p class="text-xs text-gray-600 leading-relaxed">
            Create live classes in Learnbird and automatically generate secure
            Zoom meeting IDs, passcodes, and join links without leaving the
            dashboard.
          </p>
        </div>

        <div
          class="daisy-card bg-slate-50 border border-gray-200 p-4 rounded-xl"
        >
          <div class="flex items-center gap-3 mb-2">
            <i class="fa-solid fa-desktop text-blue-600 text-base"></i>
            <h3 class="font-bold text-gray-800 text-sm">
              Seamless In-Browser & Native SDK
            </h3>
          </div>
          <p class="text-xs text-gray-600 leading-relaxed">
            Students can join live meetings directly inside the browser using
            the embedded Zoom Meeting Web SDK or launch via native mobile apps.
          </p>
        </div>

        <div
          class="daisy-card bg-slate-50 border border-gray-200 p-4 rounded-xl"
        >
          <div class="flex items-center gap-3 mb-2">
            <i class="fa-solid fa-lock text-blue-600 text-base"></i>
            <h3 class="font-bold text-gray-800 text-sm">
              Secure Access Control
            </h3>
          </div>
          <p class="text-xs text-gray-600 leading-relaxed">
            Meeting links are restricted to verified enrolled students and
            authenticated instructors to prevent unauthorized access and
            Zoombombing.
          </p>
        </div>

        <div
          class="daisy-card bg-slate-50 border border-gray-200 p-4 rounded-xl"
        >
          <div class="flex items-center gap-3 mb-2">
            <i class="fa-solid fa-sliders text-blue-600 text-base"></i>
            <h3 class="font-bold text-gray-800 text-sm">
              Host Controls & Recording Sync
            </h3>
          </div>
          <p class="text-xs text-gray-600 leading-relaxed">
            Instructors maintain complete host capabilities (mute/unmute, screen
            share, breakout rooms) and automatically sync session recordings.
          </p>
        </div>
      </div>
    </section>

    <!-- Section 2: Prerequisites -->
    <section
      id="prerequisites"
      class="scroll-mt-20 pt-6 border-t border-gray-200"
    >
      <div class="flex items-center gap-2 mb-3">
        <div
          class="w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 font-bold text-sm shrink-0"
        >
          2
        </div>
        <h2 class="text-xl font-bold text-gray-900">
          Prerequisites & Requirements
        </h2>
      </div>
      <p class="text-sm text-gray-600 leading-relaxed mb-4">
        To use the Learnbird Zoom Integration, please ensure your system and
        account meet the following criteria:
      </p>

      <ul class="space-y-3 mb-6">
        <li
          class="flex items-start gap-3 text-sm text-gray-700 bg-white border border-gray-200 p-3 rounded-lg"
        >
          <i
            class="fa-solid fa-chalkboard-user text-blue-600 mt-1 shrink-0"
          ></i>
          <div>
            <strong class="text-gray-900"
              >For Instructors & Host Accounts:</strong
            >
            <p class="text-xs text-gray-600 mt-0.5">
              An active Learnbird Instructor or Administrator account (must be 16 years of age or older) + a valid
              Zoom Account (Pro, Business, Education, or Enterprise account
              recommended for host meeting creation).
            </p>
          </div>
        </li>
        <li
          class="flex items-start gap-3 text-sm text-gray-700 bg-white border border-gray-200 p-3 rounded-lg"
        >
          <i class="fa-solid fa-user-graduate text-blue-600 mt-1 shrink-0"></i>
          <div>
            <strong class="text-gray-900">For Students & Learners:</strong>
            <p class="text-xs text-gray-600 mt-0.5">
              An active Learnbird Student account enrolled in the target live
              course. A paid Zoom account is <strong>not</strong> required for
              students to join live classes.
            </p>
          </div>
        </li>
        <li
          class="flex items-start gap-3 text-sm text-gray-700 bg-white border border-gray-200 p-3 rounded-lg"
        >
          <i class="fa-solid fa-laptop text-blue-600 mt-1 shrink-0"></i>
          <div>
            <strong class="text-gray-900"
              >Browser & System Compatibility:</strong
            >
            <p class="text-xs text-gray-600 mt-0.5">
              Web: Latest version of Chrome, Firefox, Safari, or Microsoft Edge
              with WebGL and WebRTC support. Mobile: Android (v8.0+) or iOS
              (v14.0+) with the Learnbird app installed.
            </p>
          </div>
        </li>
      </ul>
    </section>

    <!-- Section 3: Installation & Authorization -->
    <section
      id="installation"
      class="scroll-mt-20 pt-6 border-t border-gray-200"
    >
      <div class="flex items-center gap-2 mb-3">
        <div
          class="w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 font-bold text-sm shrink-0"
        >
          3
        </div>
        <h2 class="text-xl font-bold text-gray-900">
          App Installation & Authorization Process
        </h2>
      </div>
      <p class="text-sm text-gray-600 leading-relaxed mb-4">
        Follow these step-by-step instructions to connect your Zoom account to
        Learnbird:
      </p>

      <ol class="space-y-4 mb-6">
        <li class="flex gap-4 items-start">
          <span
            class="flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-gray-800 font-bold text-xs shrink-0 mt-0.5"
            >1</span
          >
          <div class="text-sm text-gray-700">
            <strong class="text-gray-900">Log in to Learnbird:</strong> Navigate
            to
            <a
              href="https://learnbird.lk"
              target="_blank"
              class="text-blue-600 hover:underline"
              >learnbird.lk</a
            >
            and sign in with your Instructor credentials.
          </div>
        </li>

        <li class="flex gap-4 items-start">
          <span
            class="flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-gray-800 font-bold text-xs shrink-0 mt-0.5"
            >2</span
          >
          <div class="text-sm text-gray-700">
            <strong class="text-gray-900">Navigate to App Management:</strong>
            From the left navigation menu, click <strong>Dashboard</strong> &gt;
            <strong>App Management</strong> (or
            <strong>Link Zoom Account</strong>).
          </div>
        </li>

        <li class="flex gap-4 items-start">
          <span
            class="flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-gray-800 font-bold text-xs shrink-0 mt-0.5"
            >3</span
          >
          <div class="text-sm text-gray-700">
            <strong class="text-gray-900"
              >Initiate Zoom OAuth Connection:</strong
            >
            Click the
            <span class="font-semibold text-blue-600"
              >"Connect Zoom Account"</span
            >
            button. You will be securely redirected to the official Zoom App
            Marketplace Authorization consent screen.
          </div>
        </li>

        <li class="flex gap-4 items-start">
          <span
            class="flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-gray-800 font-bold text-xs shrink-0 mt-0.5"
            >4</span
          >
          <div class="text-sm text-gray-700">
            <strong class="text-gray-900">Review & Grant Permissions:</strong>
            Review the requested scopes (including
            <code class="bg-gray-100 px-1 py-0.5 rounded text-xs"
              >user:read</code
            >,
            <code class="bg-gray-100 px-1 py-0.5 rounded text-xs"
              >meeting:write</code
            >, and
            <code class="bg-gray-100 px-1 py-0.5 rounded text-xs"
              >meeting:read</code
            >) necessary for creating meeting sessions on your behalf, and click
            <strong>Allow</strong>.
          </div>
        </li>

        <li class="flex gap-4 items-start">
          <span
            class="flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-gray-800 font-bold text-xs shrink-0 mt-0.5"
            >5</span
          >
          <div class="text-sm text-gray-700">
            <strong class="text-gray-900">Authorization Complete:</strong> Once
            authorized, Zoom will automatically redirect you back to Learnbird.
            You will see a success badge confirming your Zoom integration is
            active and ready for scheduling live classes.
          </div>
        </li>
      </ol>
    </section>

    <!-- Section 4: How to Use -->
    <section id="usage" class="scroll-mt-20 pt-6 border-t border-gray-200">
      <div class="flex items-center gap-2 mb-3">
        <div
          class="w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 font-bold text-sm shrink-0"
        >
          4
        </div>
        <h2 class="text-xl font-bold text-gray-900">How to Use the App</h2>
      </div>

      <div class="space-y-6">
        <!-- Sub-section: Instructors -->
        <div class="bg-slate-50 border border-gray-200 p-5 rounded-xl">
          <h3
            class="text-base font-bold text-gray-900 mb-3 flex items-center gap-2"
          >
            <i class="fa-solid fa-chalkboard-user text-blue-600"></i>
            For Instructors & Course Creators
          </h3>
          <ul
            class="space-y-2 text-xs text-gray-600 leading-relaxed list-disc pl-5"
          >
            <li>
              <strong>Scheduling a Live Class:</strong> Go to
              <em>Live Classes</em> &gt; <em>Add Live Class</em>. Fill in the
              class title, schedule date/time, and select
              <strong>Zoom</strong> as the meeting provider.
            </li>
            <li>
              <strong>Automatic Meeting Generation:</strong> Learnbird
              automatically creates the corresponding Zoom meeting under your
              connected Zoom account and generates unique join signatures for
              enrolled students.
            </li>
            <li>
              <strong>Starting a Class:</strong> When it's time for class, click
              <strong>Start Class</strong> from your Dashboard. You can host
              directly via the embedded Zoom Web SDK or open the Zoom desktop
              application.
            </li>
          </ul>
        </div>

        <!-- Sub-section: Students -->
        <div class="bg-slate-50 border border-gray-200 p-5 rounded-xl">
          <h3
            class="text-base font-bold text-gray-900 mb-3 flex items-center gap-2"
          >
            <i class="fa-solid fa-user-graduate text-blue-600"></i>
            For Students & Participants
          </h3>
          <ul
            class="space-y-2 text-xs text-gray-600 leading-relaxed list-disc pl-5"
          >
            <li>
              <strong>Joining a Session:</strong> Navigate to your
              <em>Learning Space</em> or enrolled course details page.
            </li>
            <li>
              <strong>One-Click Join:</strong> Click the
              <strong>Join Live Class</strong> button. The meeting will launch
              inside the browser window using the Zoom Web SDK with your
              registered display name.
            </li>
            <li>
              <strong>In-Class Interaction:</strong> Use audio, video, chat,
              raise hand, and screen viewing features provided within the
              embedded Zoom meeting interface.
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Section 5: Uninstallation & De-authorization -->
    <section
      id="uninstallation"
      class="scroll-mt-20 pt-6 border-t border-gray-200"
    >
      <div class="flex items-center gap-2 mb-3">
        <div
          class="w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 font-bold text-sm shrink-0"
        >
          5
        </div>
        <h2 class="text-xl font-bold text-gray-900">
          De-authorization & Uninstallation Steps
        </h2>
      </div>
      <p class="text-sm text-gray-600 leading-relaxed mb-4">
        If you wish to disconnect your Zoom account from Learnbird or remove the
        application from your Zoom account, follow these steps:
      </p>

      <div
        class="daisy-alert border border-amber-200 bg-amber-50 p-4 rounded-xl mb-6 text-amber-900"
      >
        <i class="fa-solid fa-circle-info text-amber-600 text-lg shrink-0"></i>
        <div class="text-xs leading-relaxed">
          <strong>Note:</strong> Uninstalling the app will revoke Learnbird's
          authorization to schedule new Zoom meetings on your behalf. Any
          existing scheduled live classes will revert to manual meeting links
          until re-connected.
        </div>
      </div>

      <ol class="space-y-4 mb-6">
        <li class="flex gap-4 items-start">
          <span
            class="flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-gray-800 font-bold text-xs shrink-0 mt-0.5"
            >1</span
          >
          <div class="text-sm text-gray-700">
            Log in to your Zoom Account and navigate to the
            <strong>Zoom App Marketplace</strong> at
            <a
              href="https://marketplace.zoom.us/"
              target="_blank"
              class="text-blue-600 hover:underline"
              >https://marketplace.zoom.us/</a
            >.
          </div>
        </li>

        <li class="flex gap-4 items-start">
          <span
            class="flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-gray-800 font-bold text-xs shrink-0 mt-0.5"
            >2</span
          >
          <div class="text-sm text-gray-700">
            Click <strong>Manage</strong> in the top right corner of the page,
            then select <strong>Added Apps</strong> (or search for
            <strong>Learnbird</strong>).
          </div>
        </li>

        <li class="flex gap-4 items-start">
          <span
            class="flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-gray-800 font-bold text-xs shrink-0 mt-0.5"
            >3</span
          >
          <div class="text-sm text-gray-700">
            Locate the <strong>Learnbird</strong> app and click the
            <strong>Remove</strong> / <strong>Uninstall</strong> button.
          </div>
        </li>

        <li class="flex gap-4 items-start">
          <span
            class="flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-gray-800 font-bold text-xs shrink-0 mt-0.5"
            >4</span
          >
          <div class="text-sm text-gray-700">
            Confirm the uninstallation dialog prompt when asked by Zoom.
          </div>
        </li>
      </ol>

      <div class="bg-slate-50 border border-gray-200 p-4 rounded-xl">
        <h3
          class="text-xs font-bold text-gray-800 uppercase tracking-wider mb-2"
        >
          Automated De-authorization Webhook Handling
        </h3>
        <p class="text-xs text-gray-600 leading-relaxed">
          When you uninstall the Learnbird app in Zoom Marketplace, Zoom sends
          an automated de-authorization event notification to Learnbird's
          webhook listener (<code
            class="bg-gray-200 px-1 py-0.5 rounded text-[11px]"
            >/api/zoom/deauthorize</code
          >). Upon receipt, all stored Zoom OAuth tokens and credentials
          associated with your account are immediately and permanently removed
          from our databases.
        </p>
      </div>
    </section>

    <!-- Section 6: Security & Privacy -->
    <section id="security" class="scroll-mt-20 pt-6 border-t border-gray-200">
      <div class="flex items-center gap-2 mb-3">
        <div
          class="w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 font-bold text-sm shrink-0"
        >
          6
        </div>
        <h2 class="text-xl font-bold text-gray-900">
          Data Security & Compliance
        </h2>
      </div>
      <p class="text-sm text-gray-600 leading-relaxed mb-4">
        We prioritize data protection and strictly adhere to privacy standards
        including Sri Lanka's Personal Data Protection Act, No. 9 of 2022 (PDPA)
        and Zoom Marketplace Security requirements:
      </p>

      <ul class="space-y-3 mb-6">
        <li
          class="flex items-start gap-3 text-sm text-gray-700 bg-white border border-gray-200 p-3 rounded-lg"
        >
          <i class="fa-solid fa-key text-blue-600 mt-1 shrink-0"></i>
          <div>
            <strong class="text-gray-900">Token Encryption & Storage:</strong>
            <p class="text-xs text-gray-600 mt-0.5">
              OAuth access tokens and refresh tokens are encrypted at rest using
              AES-256 encryption. Transient SDK signatures expire automatically
              after 2 hours.
            </p>
          </div>
        </li>
        <li
          class="flex items-start gap-3 text-sm text-gray-700 bg-white border border-gray-200 p-3 rounded-lg"
        >
          <i class="fa-solid fa-shield-halved text-blue-600 mt-1 shrink-0"></i>
          <div>
            <strong class="text-gray-900">Minimal Permission Scopes:</strong>
            <p class="text-xs text-gray-600 mt-0.5">
              Learnbird requests only the minimum required scopes (<code
                class="bg-gray-100 px-1 py-0.5 rounded text-xs"
                >meeting:write</code
              >,
              <code class="bg-gray-100 px-1 py-0.5 rounded text-xs"
                >meeting:read</code
              >,
              <code class="bg-gray-100 px-1 py-0.5 rounded text-xs"
                >user:read</code
              >) strictly needed to provide class hosting functionality.
            </p>
          </div>
        </li>
        <li
          class="flex items-start gap-3 text-sm text-gray-700 bg-white border border-gray-200 p-3 rounded-lg"
        >
          <i class="fa-solid fa-user-shield text-blue-600 mt-1 shrink-0"></i>
          <div>
            <strong class="text-gray-900">Privacy Policy Alignment:</strong>
            <p class="text-xs text-gray-600 mt-0.5">
              For complete details on data handling and user rights, please
              review our
              <a routerLink="/privacy" class="text-blue-600 hover:underline"
                >Privacy Policy</a
              >
              and
              <a routerLink="/terms" class="text-blue-600 hover:underline"
                >Terms and Conditions</a
              >.
            </p>
          </div>
        </li>
      </ul>
    </section>

    <!-- Section 7: Troubleshooting & Support -->
    <section
      id="troubleshooting"
      class="scroll-mt-20 pt-6 border-t border-gray-200"
    >
      <div class="flex items-center gap-2 mb-3">
        <div
          class="w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 font-bold text-sm shrink-0"
        >
          7
        </div>
        <h2 class="text-xl font-bold text-gray-900">
          Troubleshooting & Support
        </h2>
      </div>

      <div class="space-y-4 mb-8">
        <div class="bg-white border border-gray-200 p-4 rounded-xl">
          <h3 class="font-bold text-gray-900 text-sm mb-1">
            Issue: "Camera or Microphone Permission Denied"
          </h3>
          <p class="text-xs text-gray-600 leading-relaxed">
            Ensure your web browser (Chrome/Safari/Edge) has been granted
            permission to access your camera and microphone in the browser site
            settings (<code class="bg-gray-100 px-1 py-0.5 rounded"
              >chrome://settings/content</code
            >).
          </p>
        </div>

        <div class="bg-white border border-gray-200 p-4 rounded-xl">
          <h3 class="font-bold text-gray-900 text-sm mb-1">
            Issue: "Zoom Authorization Expired or Failed"
          </h3>
          <p class="text-xs text-gray-600 leading-relaxed">
            If your Zoom token expires, navigate to
            <strong>Dashboard &gt; App Management</strong>, click
            <strong>Disconnect Zoom Account</strong>, and then re-click
            <strong>Connect Zoom Account</strong> to re-authorize.
          </p>
        </div>

        <div class="bg-white border border-gray-200 p-4 rounded-xl">
          <h3 class="font-bold text-gray-900 text-sm mb-1">
            Issue: "Signature Invalid or Meeting Expired"
          </h3>
          <p class="text-xs text-gray-600 leading-relaxed">
            Meeting join signatures are valid for the scheduled time slot.
            Refresh your page or launch the meeting again from your Learnbird
            schedule.
          </p>
        </div>
      </div>

      <!-- Support Box -->
      <div
        class="bg-blue-50 border border-blue-200 p-6 rounded-2xl text-center"
      >
        <i class="fa-solid fa-headset text-blue-600 text-3xl mb-3"></i>
        <h3 class="text-base font-bold text-gray-900 mb-1">
          Need Additional Help?
        </h3>
        <p class="text-xs text-gray-600 mb-4 max-w-md mx-auto">
          Our technical support team is available to assist you with Zoom
          integration setup, troubleshooting, or general questions.
        </p>
        <div class="flex flex-wrap items-center justify-center gap-3">
          <a
            href="mailto:support@learnbird.lk"
            class="daisy-btn daisy-btn-primary daisy-btn-sm text-xs font-semibold px-4"
          >
            <i class="fa-solid fa-envelope mr-1"></i> Email Support
            (support&#64;learnbird.lk)
          </a>
          <a
            routerLink="/contact"
            class="daisy-btn daisy-btn-outline daisy-btn-sm text-xs font-semibold px-4"
          >
            <i class="fa-solid fa-comments mr-1"></i> Contact Us Form
          </a>
        </div>
      </div>
    </section>
  </main>
</div>
